class CustomProductAttributes extends HTMLElement {
  constructor() {
    super();

    this.form = null;
    this.addToCartButton = null;
    this.originalButtonText = null;
    this.originalDisabledState = null;
    this.optionSelectors = [];
    this.requiredSelections = new Set();
    this.completedSelections = new Map();
    this.isCombinedListing = false;

    this.init();
  }

  init() {
    this.isCombinedListing = this.dataset.combinedListing === 'true';
    this.refreshActiveSelectors();

    const productSection = this.closest('.product');
    if (!productSection) {
      console.warn('Custom product attributes: Product section not found');
      return;
    }

    productSection.addEventListener('on:variant:change', this.handleVariantChange.bind(this));
    this.findForm();
  }

  refreshActiveSelectors() {
    if (this.isCombinedListing) {
      const activeChild = this.querySelector('.custom-attributes__child:not([hidden])');
      this.optionSelectors = activeChild ? [...activeChild.querySelectorAll('.option-selector')] : [];
    } else {
      this.optionSelectors = [...this.querySelectorAll('.option-selector')];
    }

    this.requiredSelections = new Set(
      this.optionSelectors.map((selector) => {
        const radio = selector.querySelector('input[type="radio"]');
        const select = selector.querySelector('select');
        return radio?.name || select?.name;
      })
    );

    this.completedSelections = new Map();
  }

  findForm() {
    const productForm = document.querySelector(`#${this.dataset.productForm}`);
    if (!productForm) {
      requestAnimationFrame(() => this.findForm());
      return;
    }

    this.form = productForm;
    this.addToCartButton = this.form?.querySelector('[name="add"]')
      || document.querySelector('[data-enquire-now-button]');

    if (!this.form || !this.addToCartButton) {
      console.warn('Custom product attributes: Form elements not found');
      return;
    }

    this.originalButtonText = this.addToCartButton.dataset.addToCartText
      || this.addToCartButton.dataset.enquireText
      || this.addToCartButton.textContent;
    this.originalDisabledState = this.addToCartButton.disabled;

    this.setupEventListeners();

    // An attribute with a single value renders already selected, and nothing will ever fire a
    // change event for it. Seed its property and judge the button from the rendered state
    // instead of assuming nothing is chosen.
    this.syncPreSelected();
    this.updateAddToCartButton();
  }

  syncPreSelected() {
    if (!this.form) return;

    this.optionSelectors.forEach((selector) => {
      if (selector.dataset.selectorType === 'loft-grid') return;
      if (selector.dataset.selectorType === 'dropdown') {
        const select = selector.querySelector('select');
        const label = selector.querySelector('label');
        // Same label derivation as onSelectChange, so a later change reuses this input
        // rather than creating a second one under a differently-trimmed name.
        if (select && select.value !== '' && label) {
          this.updateHiddenInput(label.textContent, select.value);
        }
        return;
      }

      const checked = selector.querySelector('input[type="radio"]:checked');
      const legend = selector.querySelector('legend');
      if (checked && legend) this.updateHiddenInput(legend.textContent, checked.value);
    });
  }

  handleVariantChange(event) {
    if (event.detail.variant) {
      this.originalButtonText = this.addToCartButton.dataset.addToCartText || this.addToCartButton.textContent;
      this.originalDisabledState = !event.detail.variant.available;

      if (this.isCombinedListing) {
        this.swapChildAttributes(event.detail.variant.id);
      }

      this.updateAddToCartButton();
    }
  }

  swapChildAttributes(variantId) {
    const variantIdStr = variantId.toString();
    const children = this.querySelectorAll('.custom-attributes__child');

    children.forEach((child) => {
      child.hidden = child.dataset.childVariantId !== variantIdStr;
    });

    this.clearPropertyInputs();
    this.refreshActiveSelectors();
    this.setupEventListeners();
    this.syncPreSelected();
  }

  clearPropertyInputs() {
    if (!this.form) return;

    this.querySelectorAll('.option-selector legend, .option-selector > label').forEach((el) => {
      const label = el.textContent.trim();
      const input = this.form.querySelector(`input[name="properties[${label}]"]`);
      if (input) input.remove();
    });
  }

  setupEventListeners() {
    this.optionSelectors.forEach((selector) => {
      if (selector.dataset.listenersAttached) return;
      selector.dataset.listenersAttached = 'true';

      const {selectorType} = selector.dataset;

      if (selectorType === 'loft-grid') {
        this.setupLoftGrid(selector);
        return;
      }

      if (selectorType === 'dropdown') {
        const nativeSelect = selector.querySelector('select');
        if (!nativeSelect) return;

        nativeSelect.addEventListener('change', () => this.onSelectChange(nativeSelect));
        return;
      }

      if (selectorType === 'radio') {
        const radioButtons = selector.querySelectorAll('input[type="radio"]');
        if (!radioButtons.length) return;

        radioButtons.forEach((radio) => {
          radio.addEventListener('change', () => this.onOptionChange(radio));
        });
        return;
      }
    });
  }

  setupLoftGrid(grid) {
    this.loftGrid = grid;
    const qty = this.form.querySelector('quantity-input');
    if (qty) {
      qty.hidden = true;
      const qtyField = qty.querySelector('input[name="quantity"]');
      if (qtyField) qtyField.value = 1;
    }

    const loftButtons = [...grid.querySelectorAll('[data-loft]')];
    loftButtons.forEach((button) => {
      button.addEventListener('click', () => {
        button.setAttribute('aria-pressed', String(button.getAttribute('aria-pressed') !== 'true'));
        this.onLoftGridChange();
      });
    });

    const selectAllButton = grid.querySelector('[data-loft-all]');
    selectAllButton.addEventListener('click', () => {
      const selectAll = !loftButtons.every((button) => button.getAttribute('aria-pressed') === 'true');
      loftButtons.forEach((button) => button.setAttribute('aria-pressed', String(selectAll)));
      this.onLoftGridChange();
    });

    grid.querySelector('[data-loft-toggle]')?.addEventListener('click', () => {
      this.openLoftGrid();
      selectAllButton.focus();
    });

    this.onLoftGridChange();
  }

  openLoftGrid() {
    this.loftGrid.classList.add('is-open');
    this.loftGrid.querySelector('[data-loft-toggle]')?.setAttribute('aria-expanded', 'true');
  }

  getSelectedLofts() {
    if (!this.loftGrid) return [];
    return [...this.loftGrid.querySelectorAll('[data-loft][aria-pressed="true"]')].map((b) => b.dataset.loft);
  }

  onLoftGridChange() {
    const grid = this.loftGrid;
    const {property} = grid.dataset;
    const lofts = this.getSelectedLofts();
    const total = grid.querySelectorAll('[data-loft]').length;
    const fullSet = lofts.length === total;

    this.form
      .querySelectorAll(`input[name="properties[${property}]"], input[name="dg-multi[${property}]"]`)
      .forEach((input) => input.remove());
    if (lofts.length) this.updateHiddenInput(property, lofts[0]);
    lofts.forEach((loft) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = `dg-multi[${property}]`;
      input.value = loft;
      this.form.appendChild(input);
    });

    // Lofts carried over from the other hand arrive selected, so the list opens to show them.
    if (lofts.length) this.openLoftGrid();

    const str = grid.dataset;
    grid.querySelector('[data-loft-count]').textContent = str.strSelected.replace('[n]', lofts.length);
    grid.classList.toggle('DG-loft-grid--full', fullSet);
    grid.querySelector('[data-set-title]').textContent = fullSet ? str.strSetOn : str.strSet;
    grid.querySelector('[data-set-note]').textContent = fullSet ? str.strSetOnNote : str.strSetNote;
    const setButton = grid.querySelector('[data-loft-all]');
    setButton.textContent = fullSet ? str.strRemoveAll : str.strSelectAll;

    const memberCents = Number(str.memberCents);
    const useMember = memberCents > 0 && window.dcMember?.isEntitled?.() === true;
    const unitCents = useMember ? memberCents : Number(str.unitCents);
    const subtotal = grid.closest('.DG-loft-grid-wrap').querySelector('[data-loft-subtotal]');
    subtotal.textContent = new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'AUD',
    }).format((unitCents * lofts.length) / 100);
    subtotal.parentElement.hidden = lofts.length === 0;

    this.updateAddToCartButton();
  }

  loftButtonText() {
    const lofts = this.getSelectedLofts();
    const str = this.loftGrid.dataset;
    return lofts.length === 1 ? str.strAddOne : str.strAddMany.replace('[n]', lofts.length);
  }

  onSelectChange(select) {
    const legend = select.closest('.option-selector').querySelector('label').textContent;
    const {value} = select;

    this.updateHiddenInput(legend, value);
    this.updateAddToCartButton();

    this.dispatchEvent(
      new CustomEvent('custom-attribute:change', {
        bubbles: true,
        detail: {
          optionId: select.id,
          value,
          propertyName: legend,
        },
      })
    );
  }

  onOptionChange(radio) {
    const optionId = radio.name.split('-')[0];
    const {value} = radio;
    const legend = radio.closest('fieldset').querySelector('legend').textContent;

    const fieldset = radio.closest('fieldset');
    fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
      input.checked = input == radio;
    });

    this.updateHiddenInput(legend, value);
    this.updateAddToCartButton();

    this.dispatchEvent(
      new CustomEvent('custom-attribute:change', {
        bubbles: true,
        detail: {
          optionId,
          value,
          propertyName: legend,
        },
      })
    );
  }

  updateHiddenInput(legend, value) {
    let hiddenInput = this.form.querySelector(`input[name="properties[${legend}]"]`);
    if (!hiddenInput) {
      hiddenInput = document.createElement('input');
      hiddenInput.type = 'hidden';
      hiddenInput.name = `properties[${legend}]`;
      this.form.appendChild(hiddenInput);
    }
    hiddenInput.value = value;
  }

  updateAddToCartButton() {
    if (!this.addToCartButton) return;

    const allSelectionsComplete = this.checkAllSelectionsComplete();

    const unavailableStrings = [
      window.theme?.strings?.noStock,
      window.theme?.strings?.noVariant,
      window.theme?.strings?.noSelectedVariant,
    ];
    // trim(): the Liquid-rendered label carries whitespace, so an exact match missed sold-out products.
    if (unavailableStrings.includes(this.addToCartButton.textContent.trim())) {
      return;
    }

    if (allSelectionsComplete) {
      this.addToCartButton.textContent = this.loftGrid ? this.loftButtonText() : this.originalButtonText;
      if (this.originalDisabledState) {
        this.addToCartButton.setAttribute('aria-disabled', 'true');
        this.addToCartButton.disabled = true;
        return;
      }
      this.addToCartButton.removeAttribute('aria-disabled');
      this.addToCartButton.disabled = false;
      return;
    }

    this.addToCartButton.setAttribute('aria-disabled', 'true');
    this.addToCartButton.disabled = true;
    this.addToCartButton.textContent = this.loftGrid?.dataset.strEmpty
      || window.theme?.strings?.selectOptions
      || 'Please select all options';
  }

  checkAllSelectionsComplete() {
    // Left/right-hand products: Hand is required too (DG-hand-switcher.js sets data-chosen).
    if (document.querySelector('[data-dg-hand][data-chosen="false"]')) return false;
    return this.optionSelectors.every((selector) => {
      if (selector.dataset.selectorType === 'loft-grid') {
        return this.getSelectedLofts().length > 0;
      }
      if (selector.dataset.selectorType === 'dropdown') {
        const select = selector.querySelector('select');
        return select && select.value !== '';
      } else {
        const checkedInput = selector.querySelector('input[type="radio"]:checked');
        return checkedInput !== null;
      }
    });
  }
}

customElements.define('custom-product-attributes', CustomProductAttributes);
